import React, { useEffect, useState } from 'react';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { api } from '@/lib/api';
import { formatINR } from '@/lib/format';
import { IndianRupee, XCircle, UserCheck, RefreshCcw } from 'lucide-react';

const PERIODS = [['day', 'Daily'], ['month', 'Monthly'], ['year', 'Yearly']];

const Kpi = ({ label, value, sub, icon: Icon, color, testid }) => (
    <Card className="p-4" data-testid={testid}>
        <div className="flex items-center justify-between">
            <div className="text-xs text-muted-foreground">{label}</div>
            <Icon className="h-4 w-4" style={{ color }} />
        </div>
        <div className="mt-1 font-display text-xl font-semibold tabular-nums">{value}</div>
        <div className="text-xs text-muted-foreground">{sub}</div>
    </Card>
);

export const PaymentsOverview = () => {
    const [period, setPeriod] = useState('month');
    const [value, setValue] = useState('');
    const [data, setData] = useState(null);

    useEffect(() => {
        const p = new URLSearchParams({ period });
        if (value) p.set('value', value);
        api.get(`/admin/payments/overview?${p.toString()}`).then(r => setData(r.data)).catch(() => {});
    }, [period, value]);

    const switchPeriod = (p) => { setPeriod(p); setValue(''); };
    const s = data?.summary;

    return (
        <div className="mb-4" data-testid="admin-payments-overview">
            <div className="flex flex-wrap items-center gap-3 mb-3">
                <div className="inline-flex rounded-full border border-border p-0.5 bg-secondary/50">
                    {PERIODS.map(([k, label]) => (
                        <button key={k} type="button" onClick={() => switchPeriod(k)}
                            className={`px-3.5 py-1 text-xs font-medium rounded-full transition-colors ${period === k ? 'bg-[#0E2A47] text-white' : 'text-muted-foreground hover:text-foreground'}`}
                            data-testid={`admin-payments-period-${k}`}>{label}</button>
                    ))}
                </div>
                {period === 'day' && (
                    <Input type="date" value={data?.value || ''} onChange={e => setValue(e.target.value)} className="h-9 w-44" data-testid="admin-payments-period-day-input" />
                )}
                {period === 'month' && data && (
                    <Select value={data.value} onValueChange={setValue}>
                        <SelectTrigger className="h-9 w-48" data-testid="admin-payments-period-month-select"><SelectValue /></SelectTrigger>
                        <SelectContent>{data.months.map(m => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}</SelectContent>
                    </Select>
                )}
                {period === 'year' && data && (
                    <Select value={data.value} onValueChange={setValue}>
                        <SelectTrigger className="h-9 w-32" data-testid="admin-payments-period-year-select"><SelectValue /></SelectTrigger>
                        <SelectContent>{data.years.map(y => <SelectItem key={y} value={y}>{y}</SelectItem>)}</SelectContent>
                    </Select>
                )}
                {data && <span className="text-sm text-muted-foreground" data-testid="admin-payments-period-label">Showing <strong className="text-foreground">{data.label}</strong></span>}
            </div>
            {s && (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                    <Kpi label="Collection received" value={formatINR(s.received.inr)} sub={`${s.received.count} payments`} icon={IndianRupee} color="#0F9D58" testid="admin-payments-kpi-received" />
                    <Kpi label="Failed" value={formatINR(s.failed.inr)} sub={`${s.failed.count} payments`} icon={XCircle} color="#DC2626" testid="admin-payments-kpi-failed" />
                    <Kpi label="Fulfilled by admin" value={formatINR(s.fulfilled_admin.inr)} sub={`${s.fulfilled_admin.count} payments`} icon={UserCheck} color="#2563EB" testid="admin-payments-kpi-fulfilled" />
                    <Kpi label="Auto-recovered" value={formatINR(s.auto_reconciled.inr)} sub={`${s.auto_reconciled.count} captured late, processed`} icon={RefreshCcw} color="#B45309" testid="admin-payments-kpi-reconciled" />
                </div>
            )}
        </div>
    );
};
